/**
 * Status, filter, type catalogue, and named-report options for assessments.
 */
import { FileEdit, Send, CheckCircle2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';
import type {
  AssessmentFilters,
  AssessmentStatus,
  AssessmentType,
  AssessmentTypeDefinition,
  AssessmentWriterRole,
  AutoEvaluationBlock,
  AutoEvaluationMoment,
  AutoEvaluationRating,
  NamedReportTraining,
} from '@/types/assessment.types';
import { getRoleLabel } from '@/utils/role-label-utils';

/** Assessment kinds persisted in `bilans.type`. */
export const ASSESSMENT_TYPES = [
  'volunteer',
  'mission_start',
  'mid_term',
  'mission_end',
  'named',
  'intermediate',
  'other',
] as const satisfies readonly AssessmentType[];

/** Static catalogue of assessment types (labels, schema, writer, uniqueness). */
export const ASSESSMENT_TYPE_DEFINITIONS: Record<AssessmentType, AssessmentTypeDefinition> = {
  volunteer: {
    type: 'volunteer',
    label: 'Bilan volontaire',
    schemaId: 'bilan_volunteer',
    periodicity: 'anytime',
    exportFormat: 'pdf',
    establishmentRequired: true,
    targetUserRequired: true,
    allowsMultiTarget: true,
    uniquePerTarget: false,
    writtenBy: 'volunteer',
    coordinatorUserRequired: false,
  },
  mission_start: {
    type: 'mission_start',
    label: 'Bilan début de mission',
    schemaId: 'bilan_auto_evaluation',
    periodicity: 'mission_start',
    exportFormat: 'pdf',
    establishmentRequired: true,
    targetUserRequired: true,
    allowsMultiTarget: false,
    uniquePerTarget: true,
    writtenBy: 'volunteer_and_coordinator',
    coordinatorUserRequired: false,
  },
  mid_term: {
    type: 'mid_term',
    label: 'Bilan mi-parcours',
    schemaId: 'bilan_auto_evaluation',
    periodicity: 'mid_term',
    exportFormat: 'pdf',
    establishmentRequired: true,
    targetUserRequired: true,
    allowsMultiTarget: false,
    uniquePerTarget: true,
    writtenBy: 'volunteer_and_coordinator',
    coordinatorUserRequired: false,
  },
  mission_end: {
    type: 'mission_end',
    label: 'Bilan fin de mission',
    schemaId: 'bilan_auto_evaluation',
    periodicity: 'year_end',
    exportFormat: 'pdf',
    establishmentRequired: true,
    targetUserRequired: true,
    allowsMultiTarget: false,
    uniquePerTarget: true,
    writtenBy: 'volunteer_and_coordinator',
    coordinatorUserRequired: false,
  },
  named: {
    type: 'named',
    label: 'Bilan nominatif',
    schemaId: 'bilan_named',
    periodicity: 'year_end',
    exportFormat: 'pdf',
    establishmentRequired: true,
    targetUserRequired: true,
    allowsMultiTarget: false,
    uniquePerTarget: true,
    writtenBy: 'coordinator',
    coordinatorUserRequired: true,
  },
  intermediate: {
    type: 'intermediate',
    label: 'Bilan intermédiaire (direction des collèges)',
    schemaId: 'bilan_intermediate',
    periodicity: 'anytime',
    exportFormat: 'pdf',
    establishmentRequired: true,
    targetUserRequired: false,
    allowsMultiTarget: false,
    uniquePerTarget: false,
    writtenBy: 'coordinator',
    coordinatorUserRequired: true,
  },
  other: {
    type: 'other',
    label: 'Autre',
    schemaId: 'bilan_other',
    periodicity: 'anytime',
    exportFormat: 'pdf',
    establishmentRequired: true,
    targetUserRequired: false,
    allowsMultiTarget: false,
    uniquePerTarget: false,
    writtenBy: 'coordinator',
    coordinatorUserRequired: true,
  },
};

/** Who fills each type in — displayed next to the concerned person. */
export const ASSESSMENT_WRITER_LABELS_FR: Record<AssessmentWriterRole, string> = {
  get volunteer() {
    return getRoleLabel('volunteer');
  },
  get coordinator() {
    return getRoleLabel('coordinator');
  },
  get volunteer_and_coordinator() {
    return getRoleLabel('volunteer_and_coordinator');
  },
};

/** UI labels for {@link AssessmentStatus}. */
export const ASSESSMENT_STATUS_LABELS_FR: Record<AssessmentStatus, string> = {
  to_fill: 'À compléter',
  submitted: 'Soumis',
  validated: 'Validé',
};

/** Mission-cycle columns of the self-assessment grid. */
export const AUTO_EVALUATION_MOMENTS = [
  'mission_start',
  'mid_term',
  'mission_end',
] as const satisfies readonly AutoEvaluationMoment[];

/** Column headers of the grid; an assessment fills the column matching its own type. */
export const AUTO_EVALUATION_MOMENT_LABELS: Record<AutoEvaluationMoment, string> = {
  mission_start: 'Début de mission',
  mid_term: 'Mi-parcours',
  mission_end: 'Fin de mission',
};

/** UI labels for {@link AutoEvaluationRating}. */
export const AUTO_EVALUATION_RATING_LABELS: Record<AutoEvaluationRating, string> = {
  NA: 'Non acquis',
  ECA: 'En cours d’acquisition',
  A: 'Acquis',
};

/** Self-assessment grid catalogue (verbatim from the template PDF). */
export const AUTO_EVALUATION_BLOCKS: AutoEvaluationBlock[] = [
  {
    id: 'association',
    numeral: 'I',
    title: 'Agir au sein d’une association de soutien scolaire',
    items: [
      { id: 'educational_project', label: 'Connaître le projet éducatif de l’association' },
      { id: 'explain_zupdeco_action', label: 'Savoir expliquer l’action de {company}' },
    ],
  },
  {
    id: 'group_facilitation',
    numeral: 'II',
    title: 'Encadrer et animer un groupe d’adolescents',
    items: [
      { id: 'manage_group', label: 'Réussir à gérer un groupe en séance de tutorat collectif' },
      { id: 'adapt_support', label: 'Adapter le soutien aux différents élèves' },
      {
        id: 'adult_referent',
        label: 'Prendre position en tant qu’adulte référent dans sa relation aux jeunes',
      },
      {
        id: 'set_limits',
        label: 'Poser les limites et les interdits, et les faire respecter par les jeunes',
      },
      {
        id: 'respect_privacy',
        label: 'Respecter l’intimité de chaque jeune en gardant la distance nécessaire',
      },
      {
        id: 'report_harmful_behaviour',
        label:
          'Repérer les attitudes et comportements qui portent atteinte à l’élève et les signaler au tuteur ou professionnels compétents : assistante sociale, CPE, etc.',
      },
      {
        id: 'attendance_vigilance',
        label:
          'Être vigilant vis à vis de l’assiduité des élèves et savoir prendre les mesures adaptées : liste, appel parents, rdv CPE…',
      },
      { id: 'school_rules', label: 'Connaître le règlement intérieur du collège' },
    ],
  },
  {
    id: 'teamwork',
    numeral: 'III',
    title: 'Agir au sein d’une équipe',
    items: [
      { id: 'autonomy', label: 'Faire preuve d’autonomie' },
      { id: 'initiative', label: 'Faire preuve d’initiative et être force de proposition' },
      { id: 'responsibility', label: 'Faire preuve de sens des responsabilités' },
      {
        id: 'take_on_missions',
        label:
          'Prendre en charge des missions en fonction de ses compétences et centres d’intérêt : se répartir les rôles',
      },
      {
        id: 'assert_views',
        label:
          'Affirmer ses points de vue tout en prenant en compte l’avis des autres et respectant les décisions d’équipe',
      },
      {
        id: 'communicate_actions',
        label:
          'Savoir communiquer sur ses actions, ses satisfactions et difficultés auprès de son responsable et de son équipe',
      },
    ],
  },
  {
    id: 'volunteer_management',
    numeral: 'IV',
    title: 'Animer un groupe de volontaires',
    items: [
      { id: 'tutor_role', label: 'Connaître le rôle des tuteurs' },
      {
        id: 'present_project',
        label: 'Savoir présenter le projet et les objectifs {company} aux volontaires',
      },
      { id: 'welcome_volunteers', label: 'Accueillir les volontaires' },
      {
        id: 'anticipate_schedule_changes',
        label: 'Anticiper les modifications d’horaires et trouver des solutions',
      },
      {
        id: 'guarantee_sessions',
        label:
          'Se positionner en tant que garant du bon déroulement des séances : anticiper les différentes situations, rassurer, proposer des solutions, rappeler les règles de l’établissement',
      },
      {
        id: 'student_follow_up',
        label:
          'Communiquer le suivi de l’élève (conseil des professeurs, bulletins, bilan trimestriel)',
      },
      {
        id: 'analyse_volunteer_action',
        label: 'Observer et analyser l’action des volontaires, proposer des solutions alternatives',
      },
    ],
  },
  {
    id: 'communication',
    numeral: 'V',
    title: 'Communiquer',
    items: [
      {
        id: 'families_regular_exchange',
        label:
          'Echanger régulièrement avec les familles : appel téléphonique, rencontres conviviales',
      },
      {
        id: 'families_progress_info',
        label:
          'Informer les familles de l’évolution de leur enfant au sein du dispositif (appel trimestriel, mot dans le carnet..)',
      },
      {
        id: 'team_exchange',
        label: 'Echanger avec son équipe, savoir transmettre les informations et les hiérarchiser',
      },
      {
        id: 'manager_exchange',
        label:
          'Echanger avec son responsable, savoir transmettre les informations et les hiérarchiser',
      },
      {
        id: 'school_staff_exchange',
        label: 'Echanger avec les professeurs/la vie scolaire/la direction au sujet des élèves',
      },
    ],
  },
  {
    id: 'administrative',
    numeral: 'VI',
    title: 'Assurer le travail administratif du dispositif',
    items: [
      {
        id: 'admin_tools',
        label: 'Utiliser les outils administratifs : tableau excel, drive, intranet',
      },
      {
        id: 'admin_follow_up',
        label: 'Savoir anticiper, hiérarchiser et organiser le suivi administratif',
      },
      { id: 'write_report', label: 'Savoir rédiger un compte-rendu complet et/ou un mail' },
      {
        id: 'enrolment_status',
        label:
          'Connaître précisément l’état des inscriptions des élèves et des étudiants dans le dispositif',
      },
      {
        id: 'update_lists',
        label: 'Faire les mises à jour des listes d’élèves, d’étudiants, des plannings etc.',
      },
      {
        id: 'propose_tracking_tools',
        label: 'Proposer des outils de suivi des élèves et étudiants si besoin',
      },
    ],
  },
  {
    id: 'self_reflection',
    numeral: 'VII',
    title: 'Prendre du recul sur soi et sa mission',
    items: [
      {
        id: 'self_questioning',
        label: 'Se remettre en question et avoir la volonté de progresser',
      },
      {
        id: 'strengths_and_gaps',
        label:
          'Identifier ses atouts et ses manques dans l’exercice de sa mission et définir ses besoins de formation',
      },
      {
        id: 'post_volunteering_project',
        label: 'Définir un projet pour l’après volontariat en lien avec son expérience',
      },
    ],
  },
];

/** Soft-skills catalogue — verbatim from the template (section 2, column 1). */
export const SOFT_SKILLS_OPTIONS: string[] = [
  'Pédagogie',
  'Autorité naturelle et maturité',
  'Dynamisme et enthousiasme',
  'Réactivité : réponses aux emails, tél et SMS',
  'Sens de l’initiative et force de propositions',
  'Bonne présentation',
  'Écoute',
  'Diplomatie',
  'Capacité d’adaptation',
  'Rigueur',
  'Persévérance',
  'Autonomie',
  'Ténacité',
  'Confiance en soi',
  'Empathie',
  'Communication',
  'Ponctualité',
  'Reporting',
  'Patience',
  'Animation',
  'Responsabilité',
  'Management d’équipe',
];

/** Know-how catalogue — verbatim from the template (section 2, column 2). */
export const TECHNICAL_SKILLS_OPTIONS: string[] = [
  'Gestion de projet',
  'Management (étudiants et élèves)',
  'Travail d’équipe',
  'Sens de l’organisation',
  'Capacité à fédérer',
  'Gestion du temps et des tâches',
  'Gestion du stress',
];

/** Knowledge catalogue — verbatim from the template (section 2, column 3). */
export const KNOWLEDGE_OPTIONS: string[] = [
  'Connaissance du fonctionnement d’un collège',
  'Connaissance de situations sociales délicates',
  'Connaissance du fonctionnement d’une association',
  'Connaissance de l’intranet de {company}',
  'Connaissances administratives (drive, pack office...)',
  'Gestion et organisation de séances de tutorat',
];

/** Pre-filled completed-missions bullets (section 4). */
export const DEFAULT_COMPLETED_MISSIONS: string[] = [
  'Accompagner des élèves dans le cadre du dispositif "Devoirs Faits" pour renforcer leur compréhension et leur autonomie.',
  'Animer des ateliers pédagogiques, incluant des activités favorisant l’entraide et la coopération.',
  'Soutenir les enseignants en classe en apportant un appui personnalisé à des élèves en difficulté.',
];

/**
 * Template wording for the tutor overall assessment. The field stays empty in the form;
 * the exported PDF falls back to this boilerplate, so it is only used as a placeholder here.
 */
export const DEFAULT_OVERALL_ASSESSMENT =
  'Le volontaire a fait preuve d’un grand professionnalisme et d’une implication exemplaire tout au long de son engagement. Sa bienveillance, sa patience et sa capacité à s’adapter aux besoins variés des élèves ont été particulièrement remarquées. Il a su établir une relation de confiance avec les élèves, les enseignants, et les autres membres de l’équipe éducative.';

/** Completed-trainings catalogue — verbatim from the template (section 3). */
export const NAMED_REPORT_TRAININGS: NamedReportTraining[] = [
  { id: 'psc1', label: 'Formation aux Premiers Secours de niveau 1 (PSC1)', hasDate: true },
  { id: 'civic_training', label: 'Formation civique et citoyenne', hasDate: true, hasTitle: true },
  {
    id: 'civic_service_welcome',
    label: 'Formation d’accueil – mission et cadre du service civique',
    hasDate: false,
  },
  {
    id: 'zupdeco_homework_help_methodology',
    label: 'Formation {company} - Méthodologie de l’aide aux devoirs',
    hasDate: false,
  },
  {
    id: 'zupdeco_educational_project_design',
    label: 'Formation {company} - Conception de projet pédagogique',
    hasDate: false,
  },
  {
    id: 'zupdeco_specific_audiences',
    label: 'Formation {company} - Accompagnement des publics spécifiques',
    hasDate: false,
  },
  { id: 'zupdeco_parcours_avenir', label: 'Formation {company} - Parcours Avenir', hasDate: false },
];

/** Badge + icon config for an assessment status. */
export interface AssessmentStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Badge config for each assessment status. */
export const ASSESSMENT_STATUS_CONFIG: Record<AssessmentStatus, AssessmentStatusConfig> = {
  to_fill: {
    label: ASSESSMENT_STATUS_LABELS_FR.to_fill,
    icon: FileEdit,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
  },
  submitted: {
    label: ASSESSMENT_STATUS_LABELS_FR.submitted,
    icon: Send,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  validated: {
    label: ASSESSMENT_STATUS_LABELS_FR.validated,
    icon: CheckCircle2,
    badgeVariant: 'active',
  },
};

/** Statuses shown as exclusive filter chips. */
export const ASSESSMENT_STATUS_FILTER_KEYS: readonly AssessmentStatus[] = [
  'to_fill',
  'submitted',
  'validated',
];

/** Type select options from {@link ASSESSMENT_TYPE_DEFINITIONS}. */
export const ASSESSMENT_TYPE_OPTIONS = ASSESSMENT_TYPES.map((type) => ({
  value: type,
  label: ASSESSMENT_TYPE_DEFINITIONS[type].label,
}));

/** Default assessment list filters (all unset). */
export const DEFAULT_ASSESSMENT_FILTERS: AssessmentFilters = {
  type: undefined,
  status: undefined,
  establishmentId: undefined,
  search: undefined,
};

/** Filter group IDs for the assessments {@link DataTable} configure panel. */
export type AssessmentFilterGroupId =
  | 'filter-type'
  | 'filter-status'
  | 'filter-establishment'
  | 'filter-search';

/** Configure-panel order for assessment filter groups. */
export const ALL_ASSESSMENT_FILTER_GROUP_IDS: AssessmentFilterGroupId[] = [
  'filter-type',
  'filter-status',
  'filter-establishment',
  'filter-search',
];

/** Human-readable label per filter group (configure panel). */
export const ASSESSMENT_FILTER_GROUP_LABELS: Record<AssessmentFilterGroupId, string> = {
  'filter-type': 'Type de bilan',
  'filter-status': 'Statut',
  'filter-establishment': 'Établissement',
  'filter-search': 'Recherche',
};

/** React Query keys invalidated after an assessment create / save / validate. */
export const ASSESSMENT_FORM_QUERY_KEYS = {
  list: ['assessments'] as const,
  one: (id: string) => ['assessment', id] as const,
};
